import { useText } from "@/lib/content";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Send } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { usePageTitle } from "@/components/layout/AppLayout";
import { QuestionField } from "@/components/assessment/QuestionField";

const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

export default function AssessmentWizard({ preview = false }) {
    const t = useText();
    const updatedMsg = useRef("");
    updatedMsg.current = t("assessment.updated");
    usePageTitle(preview ? "Anteprima questionario" : "Assessment AI");
    const { version } = useParams();
    const navigate = useNavigate();
    const headingRef = useRef(null);
    const [q, setQ] = useState(null);
    const [answers, setAnswers] = useState({});
    const [step, setStep] = useState(0);
    const [completed, setCompleted] = useState(0);
    const [missing, setMissing] = useState([]);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        if (preview) {
            api.get(`/admin/questionnaires/${version}`).then(({ data }) => setQ(data)).catch((e) => setLoadError(errorMessage(e)));
            return;
        }
        Promise.all([api.get("/questionnaires/active"), api.get("/companies/me/assessment-draft")])
            .then(([{ data: questionnaire }, { data: d }]) => {
                setQ(questionnaire);
                if (d.discarded) {
                    setMessage({ kind: "warning", text: updatedMsg.current });
                    api.delete("/companies/me/assessment-draft");
                }
                if (d.draft && d.draft.questionnaire_version === questionnaire.version) {
                    setAnswers(d.draft.answers || {});
                    setCompleted(d.draft.completed_steps);
                    setStep(Math.min(d.draft.completed_steps, questionnaire.steps.length - 1));
                }
            })
            .catch((e) => setLoadError(errorMessage(e)));
    }, [preview, version]);

    useEffect(() => {
        if (q) headingRef.current?.focus();
    }, [step, q]);

    if (loadError) return <div className="mx-auto max-w-3xl px-4 py-12"><Notice kind="error" role="alert">{loadError}</Notice></div>;
    if (!q) return <Loading />;

    const total = q.steps.length;
    const current = q.steps[step];
    const offset = q.steps.slice(0, step).reduce((n, s) => n + s.questions.length, 0);
    const isLast = step === total - 1;

    const setAnswer = (qid, value) => {
        setAnswers((a) => ({ ...a, [qid]: value }));
        if (missing.includes(qid)) setMissing((m) => m.filter((x) => x !== qid));
    };

    const checkStep = () => {
        const miss = current.questions.filter((qu) => qu.required && isEmpty(answers[qu.id])).map((qu) => qu.id);
        setMissing(miss);
        if (miss.length) {
            setMessage({ kind: "error", text: "Rispondi a tutte le domande obbligatorie" });
            setTimeout(() => document.getElementById(`question-${miss[0]}`)?.focus(), 50);
            return false;
        }
        return true;
    };

    const next = async () => {
        setMessage(null);
        if (!checkStep()) return;
        if (preview) {
            setStep(step + 1);
            return;
        }
        setBusy(true);
        try {
            const done = Math.max(completed, step + 1);
            await api.put("/companies/me/assessment-draft", { answers, completed_steps: done });
            setCompleted(done);
            setStep(step + 1);
            window.scrollTo(0, 0);
        } catch (err) {
            setMessage({ kind: "error", text: errorMessage(err, "Salvataggio della bozza non riuscito, riprova") });
        } finally {
            setBusy(false);
        }
    };

    const back = () => {
        setMessage(null);
        setMissing([]);
        setStep(step - 1);
        window.scrollTo(0, 0);
    };

    const submit = async () => {
        setMessage(null);
        if (!checkStep()) return;
        if (preview) {
            setMessage({ kind: "info", text: "Anteprima: le risposte non vengono salvate" });
            return;
        }
        setBusy(true);
        try {
            await api.post("/companies/me/assessments", { answers });
            navigate("/profilo?tab=assessment", { state: { submitted: true } });
        } catch {
            setMessage({ kind: "error", text: "Invio non riuscito, riprova" });
            setBusy(false);
        }
    };

    return (
        <div className="edi-plainbg edi-fill" data-testid="assessment-dotgrid">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
            {preview ? (
                <Notice kind="warning" testId="preview-banner"><p className="font-semibold">Anteprima della versione {q.version}: così le aziende vedranno il questionario. Le risposte non vengono salvate.</p>
                    <Link to="/amministrazione/questionario" data-testid="preview-back-link">Torna al questionario</Link></Notice>
            ) : (
                <Link to="/profilo?tab=assessment" className="inline-flex items-center gap-1" data-testid="wizard-back-to-profile-link">
                    <ArrowLeft aria-hidden="true" size={16} /> Torna al profilo
                </Link>
            )}
            {step === 0 && !preview && <p className="mt-6 text-edi-muted whitespace-pre-line" data-testid="wizard-intro">{t("assessment.intro")}</p>}
            <p className="edi-eyebrow mt-6">{q.title}</p>
            <h1 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl sm:text-4xl font-extrabold outline-none" data-testid="wizard-step-heading">
                <span className="block text-base sm:text-lg font-bold text-edi-muted" data-testid="wizard-step-indicator">Step {step + 1} di {total}</span>
                {current.title}
            </h1>
            <div className="mt-4 flex gap-1.5" aria-hidden="true">
                {q.steps.map((s, i) => <div key={s.title} className={`h-2.5 flex-1 rounded-md ${i <= step ? "bg-edi-lime" : "bg-edi-track"}`} />)}
            </div>
            <p className="edi-help mt-3">Le risposte vengono salvate ogni volta che passi allo step successivo: puoi interrompere e riprendere in seguito.</p>

            <div aria-live="assertive" className="mt-6" data-testid="wizard-message">
                {message && <Notice kind={message.kind} role="alert" testId={message.kind === "error" ? "wizard-error-message" : "wizard-info-message"}>{message.text}</Notice>}
            </div>

            <div className="mt-6 space-y-6 edi-card p-3 sm:p-4">
                {current.questions.map((qu, i) => (
                    <QuestionField
                        key={qu.id}
                        question={qu}
                        number={offset + i + 1}
                        value={answers[qu.id]}
                        onChange={(v) => setAnswer(qu.id, v)}
                        error={missing.includes(qu.id) ? "Risposta obbligatoria" : null}
                    />
                ))}
            </div>

            <div className="mt-8 flex flex-wrap justify-between gap-3">
                {step > 0 ? (
                    <button type="button" className="btn-tertiary" onClick={back} disabled={busy} data-testid="wizard-prev-button"><ArrowLeft aria-hidden="true" size={18} /> Indietro</button>
                ) : <span />}
                {isLast ? (
                    <button type="button" className="btn-primary" onClick={submit} disabled={busy} data-testid="wizard-submit-button"><Send aria-hidden="true" size={18} /> {busy ? "Invio in corso…" : "Invia"}</button>
                ) : (
                    <button type="button" className="btn-primary" onClick={next} disabled={busy} data-testid="wizard-next-button">Avanti <ArrowRight aria-hidden="true" size={18} /></button>
                )}
            </div>
        </div>
        </div>
    );
}
